import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

// Subtle yarn-texture overlay using a repeating diagonal crosshatch at low opacity
const YARN_TEXTURE_STYLE = {
  backgroundImage: `
    repeating-linear-gradient(
      45deg,
      rgba(255,255,255,0.07) 0px,
      rgba(255,255,255,0.07) 1px,
      transparent 1px,
      transparent 6px
    ),
    repeating-linear-gradient(
      -45deg,
      rgba(0,0,0,0.06) 0px,
      rgba(0,0,0,0.06) 1px,
      transparent 1px,
      transparent 6px
    )
  `,
};

function ColorPaletteInspiration({ colors, isAdmin, onBack }) {
  const [palettes, setPalettes] = useState([]);
  const [colorsByPaletteId, setColorsByPaletteId] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedPaletteId, setExpandedPaletteId] = useState(null);
  const [editingPaletteId, setEditingPaletteId] = useState(null);
  const [showAddPalette, setShowAddPalette] = useState(false);
  const [newPaletteDraft, setNewPaletteDraft] = useState({ name: '', description: '', icon: '🎨', tags: '' });
  const [newColorDraft, setNewColorDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [hoveredSwatch, setHoveredSwatch] = useState(null); // { paletteId, colorIdx }
  const [paletteSearch, setPaletteSearch] = useState('');
  const [paletteSearchResults, setPaletteSearchResults] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    const [{ data: palettesData }, { data: paletteYarnData }] = await Promise.all([
      supabase.from('color_palettes').select('*').eq('is_published', true).order('sort_order'),
      supabase.from('palette_yarn')
        .select('id, sort_order, palette_id, yarn_library(id, brand_name, colorway_name, brand_url, image_url, hex, yards_per_skein)')
        .order('sort_order'),
    ]);
    setPalettes(palettesData || []);
    const grouped = {};
    (paletteYarnData || []).forEach(row => {
      if (!grouped[row.palette_id]) grouped[row.palette_id] = [];
      grouped[row.palette_id].push({
        ...row.yarn_library,
        palette_yarn_id: row.id,
        sort_order: row.sort_order,
        palette_id: row.palette_id,
      });
    });
    setColorsByPaletteId(grouped);
    setLoading(false);
  }

  async function handleSavePalette(palette) {
    setSaving(true);
    const payload = {
      id: palette.id,
      name: palette.name,
      description: palette.description,
      icon: palette.icon || '🎨',
      tags: palette.tags,
      sort_order: palette.sort_order || 0,
    };
    await supabase.from('color_palettes').upsert(payload);
    await fetchData();
    setEditingPaletteId(null);
    setSaving(false);
  }

  async function handleAddPalette() {
    if (!newPaletteDraft.name.trim()) return;
    setSaving(true);
    const tags = newPaletteDraft.tags
      ? newPaletteDraft.tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];
    await supabase.from('color_palettes').insert({
      name: newPaletteDraft.name.trim(),
      description: newPaletteDraft.description.trim(),
      icon: newPaletteDraft.icon || '🎨',
      tags,
      sort_order: palettes.length,
    });
    setNewPaletteDraft({ name: '', description: '', icon: '🎨', tags: '' });
    setShowAddPalette(false);
    await fetchData();
    setSaving(false);
  }

  async function handleDeletePalette(id) {
    if (!window.confirm('Delete this palette and all its colors?')) return;
    // palette_yarn rows cascade-delete; just null out patterns.palette_id then delete
    await supabase.from('patterns').update({ palette_id: null }).eq('palette_id', id);
    await supabase.from('color_palettes').delete().eq('id', id);
    await fetchData();
  }

  async function handleAddColor(paletteId) {
    const draft = newColorDraft[paletteId] || {};
    if (!draft.colorway_name?.trim() || !draft.brand_name?.trim()) return;
    setSaving(true);
    const existing = colorsByPaletteId[paletteId] || [];
    const { data: newYarn } = await supabase.from('yarn_library').insert({
      colorway_name: draft.colorway_name.trim(),
      brand_name: draft.brand_name.trim(),
      brand_url: draft.brand_url?.trim() || null,
      hex: draft.hex?.trim() || '#D6CFC7',
      image_url: draft.image_url?.trim() || null,
      yards_per_skein: parseInt(draft.yards_per_skein) || null,
    }).select().single();
    if (newYarn) {
      await supabase.from('palette_yarn').insert({
        palette_id: paletteId,
        yarn_library_id: newYarn.id,
        sort_order: existing.length,
      });
    }
    setNewColorDraft(prev => ({ ...prev, [paletteId]: {} }));
    await fetchData();
    setSaving(false);
  }

  async function handleDeleteColor(paletteYarnId) {
    // Remove from junction table only — yarn stays in the library
    await supabase.from('palette_yarn').delete().eq('id', paletteYarnId);
    await fetchData();
  }

  // Link an existing yarn_library entry to a palette
  async function handleLinkFromLibrary(paletteId, yarn) {
    const existing = colorsByPaletteId[paletteId] || [];
    await supabase.from('palette_yarn').insert({
      palette_id: paletteId,
      yarn_library_id: yarn.id,
      sort_order: existing.length,
    });
    setPaletteSearch('');
    setPaletteSearchResults([]);
    await fetchData();
  }

  // Debounced library search for palette color picker
  useEffect(() => {
    if (!paletteSearch.trim()) { setPaletteSearchResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from('yarn_library')
        .select('id, brand_name, colorway_name, brand_url, image_url, yards_per_skein')
        .or(`brand_name.ilike.%${paletteSearch}%,colorway_name.ilike.%${paletteSearch}%`)
        .limit(8);
      setPaletteSearchResults(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [paletteSearch]);

  const inputStyle = {
    padding: '0.5rem 0.75rem',
    border: `1px solid ${colors.borderGreige}`,
    borderRadius: '6px',
    fontSize: '0.875rem',
    color: colors.charcoal,
    background: '#fff',
    width: '100%',
    boxSizing: 'border-box',
  };

  const btnStyle = (variant = 'primary') => ({
    padding: '0.5rem 1rem',
    background: variant === 'primary' ? colors.sageGreen : variant === 'danger' ? '#e53e3e' : colors.bgCream,
    color: variant === 'secondary' ? colors.brownGray : '#fff',
    border: variant === 'secondary' ? `1px solid ${colors.borderGreige}` : 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: '600',
    cursor: 'pointer',
  });

  if (loading) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '3rem', textAlign: 'center', color: colors.brownGray }}>
        Loading palettes...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {onBack && (
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: colors.sageGreen, fontSize: '1rem', fontWeight: '600', cursor: 'pointer', marginBottom: '1rem' }}>
          ← Back
        </button>
      )}

      {/* Admin button */}
      {isAdmin && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowAddPalette(v => !v)} style={btnStyle('primary')}>
            + Add Palette
          </button>
        </div>
      )}

      {/* Add Palette Form */}
      {isAdmin && showAddPalette && (
        <div style={{ background: colors.bgCream, border: `2px solid ${colors.borderGreige}`, borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 1rem', color: colors.charcoal, fontSize: '1.1rem', fontWeight: '700' }}>New Palette</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <input style={inputStyle} placeholder="Palette name *" value={newPaletteDraft.name} onChange={e => setNewPaletteDraft(p => ({ ...p, name: e.target.value }))} />
            <input style={inputStyle} placeholder="Icon (emoji)" value={newPaletteDraft.icon} onChange={e => setNewPaletteDraft(p => ({ ...p, icon: e.target.value }))} />
            <input style={{ ...inputStyle, gridColumn: '1 / -1' }} placeholder="Description" value={newPaletteDraft.description} onChange={e => setNewPaletteDraft(p => ({ ...p, description: e.target.value }))} />
            <input style={{ ...inputStyle, gridColumn: '1 / -1' }} placeholder="Tags (comma-separated, e.g. Blankets, Throws)" value={newPaletteDraft.tags} onChange={e => setNewPaletteDraft(p => ({ ...p, tags: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={handleAddPalette} style={btnStyle('primary')} disabled={saving}>Save</button>
            <button onClick={() => setShowAddPalette(false)} style={btnStyle('secondary')}>Cancel</button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {palettes.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: colors.brownGray }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎨</div>
          <p style={{ fontSize: '1.1rem' }}>No palettes yet.{isAdmin ? ' Add one above!' : ' Check back soon!'}</p>
        </div>
      )}

      {/* Palette Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
        {palettes.map(palette => {
          const paletteColors = colorsByPaletteId[palette.id] || [];
          const isExpanded = expandedPaletteId === palette.id;
          const isEditing = editingPaletteId === palette.id;
          const colorDraft = newColorDraft[palette.id] || {};

          return (
            <div
              key={palette.id}
              style={{
                background: '#fff',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.07)',
                border: `2px solid ${colors.borderGreige}`,
              }}
            >
              {/* Swatches row */}
              <div style={{ display: 'flex', height: '90px', position: 'relative' }}>
                {paletteColors.length === 0 ? (
                  <div style={{ flex: 1, background: colors.bgCream, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.brownGray, fontSize: '0.85rem' }}>
                    No colors yet
                  </div>
                ) : (
                  paletteColors.map((color, idx) => {
                    const isHovered = hoveredSwatch?.paletteId === palette.id && hoveredSwatch?.colorIdx === idx;
                    const Wrapper = color.brand_url ? 'a' : 'div';
                    const wrapperProps = color.brand_url
                      ? { href: color.brand_url, target: '_blank', rel: 'noreferrer', style: { flex: 1, position: 'relative', cursor: 'pointer', textDecoration: 'none', display: 'block' } }
                      : { style: { flex: 1, position: 'relative', cursor: 'default' } };

                    return (
                      <Wrapper
                        key={color.id}
                        {...wrapperProps}
                        onMouseEnter={() => setHoveredSwatch({ paletteId: palette.id, colorIdx: idx })}
                        onMouseLeave={() => setHoveredSwatch(null)}
                      >
                        {color.image_url ? (
                          <img
                            src={color.image_url}
                            alt={`${color.brand_name} ${color.colorway_name}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        ) : (
                          <div style={{ width: '100%', height: '100%', background: color.hex, ...YARN_TEXTURE_STYLE }} />
                        )}
                        {/* Hover tooltip */}
                        {isHovered && (
                          <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(30,30,30,0.92)',
                            color: '#fff',
                            padding: '0.4rem 0.65rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: 100,
                            lineHeight: '1.4',
                            marginBottom: '4px',
                          }}>
                            <div style={{ fontWeight: '700' }}>{color.brand_name}</div>
                            <div>{color.colorway_name}</div>
                            {color.brand_url && <div style={{ opacity: 0.75, fontSize: '0.7rem' }}>Shop ↗</div>}
                          </div>
                        )}
                      </Wrapper>
                    );
                  })
                )}
              </div>

              {/* Card body */}
              <div style={{ padding: '1.25rem' }}>
                {isEditing ? (
                  /* Edit form */
                  <EditPaletteForm
                    palette={palette}
                    colors={colors}
                    inputStyle={inputStyle}
                    btnStyle={btnStyle}
                    saving={saving}
                    onSave={handleSavePalette}
                    onCancel={() => setEditingPaletteId(null)}
                  />
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>{palette.icon}</span>
                      <h3 style={{ fontSize: '1.15rem', fontWeight: '700', color: colors.charcoal, margin: 0 }}>
                        {palette.name}
                      </h3>
                    </div>
                    {palette.description && (
                      <p style={{ fontSize: '0.9rem', color: colors.darkGray, margin: '0 0 0.75rem', lineHeight: '1.5' }}>
                        {palette.description}
                      </p>
                    )}
                    {palette.tags?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
                        {palette.tags.map((tag, i) => (
                          <span key={i} style={{ padding: '0.2rem 0.6rem', background: colors.bgCream, color: colors.brownGray, borderRadius: '50px', fontSize: '0.78rem', fontWeight: '600' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setExpandedPaletteId(isExpanded ? null : palette.id)}
                        style={{ background: 'none', border: 'none', color: colors.sageGreen, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', padding: 0 }}
                      >
                        {isExpanded ? 'Hide colors ↑' : `View ${paletteColors.length} colors ↓`}
                      </button>
                      {isAdmin && (
                        <>
                          <button onClick={() => setEditingPaletteId(palette.id)} style={{ background: 'none', border: 'none', color: colors.brownGray, fontSize: '0.82rem', cursor: 'pointer', padding: 0 }}>Edit</button>
                          <button onClick={() => handleDeletePalette(palette.id)} style={{ background: 'none', border: 'none', color: '#e53e3e', fontSize: '0.82rem', cursor: 'pointer', padding: 0 }}>Delete</button>
                        </>
                      )}
                    </div>
                  </>
                )}

                {/* Expanded color list */}
                {isExpanded && !isEditing && (
                  <div style={{ marginTop: '1rem', borderTop: `1px solid ${colors.borderGreige}`, paddingTop: '1rem' }}>
                    {paletteColors.map(color => (
                      <div key={color.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.6rem' }}>
                        {color.image_url ? (
                          <img
                            src={color.image_url}
                            alt={`${color.brand_name} ${color.colorway_name}`}
                            style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0, border: `1px solid ${colors.borderGreige}` }}
                          />
                        ) : (
                          <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: color.hex, flexShrink: 0, border: `1px solid ${colors.borderGreige}`, ...YARN_TEXTURE_STYLE }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: colors.charcoal }}>{color.brand_name}</div>
                          <div style={{ fontSize: '0.8rem', color: colors.brownGray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{color.colorway_name}</div>
                        </div>
                        {color.brand_url && (
                          <a href={color.brand_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', color: colors.sageGreen, fontWeight: '600', textDecoration: 'none', flexShrink: 0 }}>
                            Shop ↗
                          </a>
                        )}
                        {isAdmin && (
                          <button onClick={() => handleDeleteColor(color.palette_yarn_id)} style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '1rem', padding: '0 0.25rem', flexShrink: 0 }}>×</button>
                        )}
                      </div>
                    ))}

                    {/* Admin: Add color */}
                    {isAdmin && (
                      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: `1px dashed ${colors.borderGreige}` }}>
                        {/* Library search */}
                        <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                          <input
                            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', borderColor: colors.sageGreen }}
                            placeholder="Search yarn library to add…"
                            value={paletteSearch}
                            onChange={e => setPaletteSearch(e.target.value)}
                          />
                          {paletteSearchResults.length > 0 && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${colors.borderGreige}`, borderRadius: '8px', zIndex: 10, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                              {paletteSearchResults.map(r => (
                                <div
                                  key={r.id}
                                  onClick={() => handleLinkFromLibrary(palette.id, r)}
                                  style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: `1px solid ${colors.borderGreige}` }}
                                  onMouseEnter={e => e.currentTarget.style.background = colors.bgCream}
                                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                >
                                  {r.image_url
                                    ? <img src={r.image_url} alt="" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                                    : <div style={{ width: '36px', height: '36px', background: colors.borderGreige, borderRadius: '6px', flexShrink: 0 }} />
                                  }
                                  <div>
                                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: colors.charcoal }}>{r.brand_name}</div>
                                    <div style={{ fontSize: '0.8rem', color: colors.brownGray }}>{r.colorway_name}{r.yards_per_skein ? ` · ${r.yards_per_skein} yds/skein` : ''}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: colors.brownGray, marginBottom: '0.5rem' }}>Or add a new yarn to the library:</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <input
                            style={inputStyle}
                            placeholder="Colorway name *"
                            value={colorDraft.colorway_name || ''}
                            onChange={e => setNewColorDraft(p => ({ ...p, [palette.id]: { ...p[palette.id], colorway_name: e.target.value } }))}
                          />
                          <input
                            style={inputStyle}
                            placeholder="Brand name *"
                            value={colorDraft.brand_name || ''}
                            onChange={e => setNewColorDraft(p => ({ ...p, [palette.id]: { ...p[palette.id], brand_name: e.target.value } }))}
                          />
                          <input
                            style={inputStyle}
                            placeholder="Hex color (optional, e.g. #B7410E)"
                            value={colorDraft.hex || ''}
                            onChange={e => setNewColorDraft(p => ({ ...p, [palette.id]: { ...p[palette.id], hex: e.target.value } }))}
                          />
                          <input
                            style={inputStyle}
                            type="number"
                            placeholder="Yards per skein (optional)"
                            value={colorDraft.yards_per_skein || ''}
                            onChange={e => setNewColorDraft(p => ({ ...p, [palette.id]: { ...p[palette.id], yards_per_skein: e.target.value } }))}
                          />
                          <input
                            style={inputStyle}
                            placeholder="Product page URL (optional)"
                            value={colorDraft.brand_url || ''}
                            onChange={e => setNewColorDraft(p => ({ ...p, [palette.id]: { ...p[palette.id], brand_url: e.target.value } }))}
                          />
                          <input
                            style={{ ...inputStyle, gridColumn: '1 / -1' }}
                            placeholder="Swatch image URL (optional — paste yarn photo URL)"
                            value={colorDraft.image_url || ''}
                            onChange={e => setNewColorDraft(p => ({ ...p, [palette.id]: { ...p[palette.id], image_url: e.target.value } }))}
                          />
                        </div>
                        <button onClick={() => handleAddColor(palette.id)} style={btnStyle('primary')} disabled={saving}>
                          + Add Color
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EditPaletteForm({ palette, inputStyle, btnStyle, saving, onSave, onCancel }) {
  const [draft, setDraft] = useState({
    ...palette,
    tags: (palette.tags || []).join(', '),
  });

  function handleSave() {
    const tags = draft.tags
      ? draft.tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];
    onSave({ ...draft, tags });
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <input style={inputStyle} placeholder="Name *" value={draft.name} onChange={e => setDraft(p => ({ ...p, name: e.target.value }))} />
        <input style={inputStyle} placeholder="Icon" value={draft.icon || ''} onChange={e => setDraft(p => ({ ...p, icon: e.target.value }))} />
        <input style={{ ...inputStyle, gridColumn: '1 / -1' }} placeholder="Description" value={draft.description || ''} onChange={e => setDraft(p => ({ ...p, description: e.target.value }))} />
        <input style={{ ...inputStyle, gridColumn: '1 / -1' }} placeholder="Tags (comma-separated)" value={draft.tags || ''} onChange={e => setDraft(p => ({ ...p, tags: e.target.value }))} />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={handleSave} style={btnStyle('primary')} disabled={saving}>Save</button>
        <button onClick={onCancel} style={btnStyle('secondary')}>Cancel</button>
      </div>
    </div>
  );
}

export default ColorPaletteInspiration;
